/**
 * Rive Canvas Component
 *
 * Light DOM Web Component that lazy-loads the Rive runtime from CDN
 * and renders a .riv animation on a <canvas> element.
 *
 * Usage:
 *   <rive-canvas src="/path/to/animation.riv" artboard="MyArtboard" state-machine="MySM">
 *     <canvas></canvas>
 *   </rive-canvas>
 *
 * The Rive CDN runtime (~150KB) is fetched only once and cached by the browser.
 *
 * ------------------------------------------------------------------
 * LAZY_LOAD toggle:
 * Set to `true` to defer loading until the element scrolls into view
 * (uses IntersectionObserver with a 200px root margin).
 * Set to `false` to load immediately when the element connects to the DOM.
 *
 * If you experience a visible "pop-in" delay as users scroll, simply
 * flip this to `false` — no other changes needed.
 * ------------------------------------------------------------------
 */
const LAZY_LOAD = true

const RIVE_CDN = 'https://unpkg.com/@rive-app/canvas@2.34.3/rive.js'

// Shared promise so the runtime script is only loaded once across all instances.
let riveLoadPromise = null

function loadRiveRuntime() {
	if (window.rive) return Promise.resolve()

	if (!riveLoadPromise) {
		riveLoadPromise = new Promise((resolve, reject) => {
			const script = document.createElement('script')
			script.src = RIVE_CDN
			script.async = true
			script.onload = () => resolve()
			script.onerror = () => reject(new Error('Failed to load Rive runtime from CDN'))
			document.head.appendChild(script)
		})
	}

	return riveLoadPromise
}

class RiveCanvas extends HTMLElement {
	connectedCallback() {
		// Prevent double-init if the element is moved in the DOM.
		if (this._initialized) return
		this._initialized = true

		if (LAZY_LOAD) {
			this._setupObserver()
		} else {
			this._init()
		}
	}

	disconnectedCallback() {
		this._cleanup()
	}

	/**
	 * IntersectionObserver-based lazy loading.
	 * Starts loading 200px before the element enters the viewport.
	 * To remove: delete this method and the LAZY_LOAD branch in connectedCallback.
	 */
	_setupObserver() {
		this._observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) {
						this._observer.disconnect()
						this._observer = null
						this._init()
						return
					}
				}
			},
			{ rootMargin: '200px' }
		)
		this._observer.observe(this)
	}

	async _init() {
		const src = this.getAttribute('src')
		if (!src) return

		const canvas = this.querySelector('canvas')
		if (!canvas) return

		// Ensure canvas fills its container width.
		// Height is intentionally left as auto — it will be driven by the
		// artboard's aspect ratio once Rive loads (see onLoad below).
		canvas.style.width = '100%'
		canvas.style.height = 'auto'
		canvas.style.display = 'block'

		// Override h-full on the host element (height:100% collapses to 0 when
		// the parent chain has no explicit height) and clip drawing overflow.
		this.style.height = 'auto'
		this.style.overflow = 'hidden'

		try {
			await loadRiveRuntime()

			const r = window.rive

			// Build options following Rive's official recommended pattern.
			const options = {
				src,
				canvas,
				autoplay: true,
				stateMachines: this.getAttribute('state-machine') || 'State Machine 1',
				layout: new r.Layout({
					fit: r.Fit.Cover,
					alignment: r.Alignment.Center,
				}),
				onLoad: () => {
					requestAnimationFrame(() => {
						// Lock the canvas to the artboard's natural aspect ratio so
						// height is driven by width (no explicit height needed on
						// the container).
						const { maxX, maxY } = this._rive.bounds
						if (maxX && maxY) {
							canvas.style.aspectRatio = `${maxX} / ${maxY}`
						}
						this._rive.resizeDrawingSurfaceToCanvas()
					})
				},
			}

			const artboard = this.getAttribute('artboard')
			if (artboard) {
				options.artboard = artboard
			}

			this._rive = new r.Rive(options)

			// Keep the canvas in sync whenever the container resizes
			// (e.g. orientation change, responsive layout reflow on mobile).
			this._resizeObserver = new ResizeObserver(() => {
				if (this._rive) {
					this._rive.resizeDrawingSurfaceToCanvas()
				}
			})
			// Observe the parent rather than this element — since rive-canvas
			// height is now driven by its canvas content, observing itself
			// would trigger a resize loop.
			this._resizeObserver.observe(this.parentElement ?? this)
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error('[rive-canvas] Failed to initialize:', err)
		}
	}

	_cleanup() {
		if (this._observer) {
			this._observer.disconnect()
			this._observer = null
		}
		if (this._resizeObserver) {
			this._resizeObserver.disconnect()
			this._resizeObserver = null
		}
		if (this._rive) {
			this._rive.cleanup()
			this._rive = null
		}
		this._initialized = false
	}
}

customElements.define('rive-canvas', RiveCanvas)
